import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9s92m4nlsngrm4lmidg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    const name = (formData.get('name') as string)?.trim();
    const email = (formData.get('email') as string)?.trim();
    const message = (formData.get('message') as string)?.trim();

    if (!name || !email || !message) {
      setFormStatus('error');
      setErrorMsg('请填写所有必填字段');
      return;
    }

    if (message.length > 500) {
      setFormStatus('error');
      setErrorMsg('留言内容不能超过500个字符');
      return;
    }

    setFormStatus('loading');

    try {
      const payload = new URLSearchParams();
      payload.append('name', name);
      payload.append('email', email);
      payload.append('message', message);
      payload.append('source', 'about-page');

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // raw text response
      }

      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || responseText
          || '提交失败，请稍后重试';
        setFormStatus('error');
        setErrorMsg(String(msg));
      }
    } catch {
      setFormStatus('error');
      setErrorMsg('网络异常，请检查连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Brand Intro */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground-900 mb-4">
                关于色彩美学
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                我们是一群热爱色彩与设计的设计师和研究者，致力于为中国设计师提供最专业的女性网站色彩设计知识
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  我们的使命
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  色彩美学（ColorAesthetics）成立于2024年，是国内首家专注于女性网站色彩设计的专业知识平台。我们发现，尽管女性消费市场蓬勃发展，但针对女性向网站设计的专业色彩指导却严重匮乏。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  许多设计师在为女性品牌设计网站时，只能凭借直觉和个人审美来选择配色，缺乏系统的方法论支撑。我们成立的初衷，就是填补这一空白——将色彩理论、心理学研究和实战经验融合，为中国设计师提供可落地的、高质量的配色指导。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  至今，我们已经分析了超过300个国内外女性品牌的色彩体系，发布了近20篇深度文章，累计为超过50,000名设计师提供了配色灵感与实操方案。
                </p>
              </div>
              <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20design%20studio%20workspace%20with%20color%20swatches%20and%20inspiration%20boards%20on%20the%20wall%2C%20warm%20natural%20lighting%20from%20large%20windows%2C%20minimal%20creative%20workspace%2C%20feminine%20aesthetic%2C%20editorial%20quality&width=800&height=600&seq=about-brand-mission&orientation=landscape"
                  alt="色彩美学设计工作室"
                  title="色彩美学 - 专注于女性网站色彩设计"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats / Reputation */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 text-center mb-10 md:mb-14">
              我们的影响力
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              <div className="text-center p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="text-3xl md:text-4xl font-heading font-semibold text-accent-500 mb-2">300+</div>
                <div className="text-sm text-foreground-600">品牌案例分析</div>
              </div>
              <div className="text-center p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="text-3xl md:text-4xl font-heading font-semibold text-accent-500 mb-2">19</div>
                <div className="text-sm text-foreground-600">深度原创文章</div>
              </div>
              <div className="text-center p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="text-3xl md:text-4xl font-heading font-semibold text-accent-500 mb-2">50K+</div>
                <div className="text-sm text-foreground-600">设计师读者</div>
              </div>
              <div className="text-center p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="text-3xl md:text-4xl font-heading font-semibold text-accent-500 mb-2">98%</div>
                <div className="text-sm text-foreground-600">读者好评率</div>
              </div>
            </div>

            <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              <div className="p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 flex-shrink-0 mt-0.5">
                    <i className="ri-shield-check-line text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">专业可信</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      所有内容均由具有5年以上女性品牌设计经验的设计师撰写与审核，确保方法论的专业性与实操性。
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 flex-shrink-0 mt-0.5">
                    <i className="ri-lightbulb-line text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">数据驱动</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      我们的配色建议基于对300+品牌的量化分析，结合色彩心理学研究，而非主观审美判断。
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 flex-shrink-0 mt-0.5">
                    <i className="ri-user-heart-line text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">以用户为中心</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      我们始终以女性用户的视觉偏好为核心，确保配色方案在提升美感的同时，也优化用户体验和转化。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 text-center mb-10 md:mb-14">
              核心团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20designer%20portrait%20with%20elegant%20style%2C%20warm%20studio%20lighting%2C%20minimal%20cream%20background%2C%20artistic%20headshot%20photography%2C%20soft%20natural%20expression&width=200&height=200&seq=team-lin-ruoxi&orientation=squarish"
                    alt="林若溪"
                    title="林若溪 - 创始人 & 主编"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">林若溪</h3>
                <p className="text-xs text-primary-500 font-medium mb-2">创始人 &amp; 主编</p>
                <p className="text-xs text-foreground-500 leading-relaxed">前国际4A设计总监，10年女性品牌设计经验</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20UX%20researcher%20portrait%2C%20modern%20creative%20workspace%20background%2C%20warm%20lighting%2C%20minimal%20style%2C%20artistic%20headshot%20photography%2C%20confident%20smile&width=200&height=200&seq=team-chen-yating&orientation=squarish"
                    alt="陈雅婷"
                    title="陈雅婷 - 研究总监"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">陈雅婷</h3>
                <p className="text-xs text-primary-500 font-medium mb-2">研究总监</p>
                <p className="text-xs text-foreground-500 leading-relaxed">色彩心理学研究者，专注女性消费行为分析</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20UI%20designer%20portrait%2C%20bright%20modern%20studio%20lighting%2C%20minimal%20background%2C%20creative%20artistic%20headshot%2C%20warm%20friendly%20expression&width=200&height=200&seq=team-wang-sihan&orientation=squarish"
                    alt="王思涵"
                    title="王思涵 - 资深设计师"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">王思涵</h3>
                <p className="text-xs text-primary-500 font-medium mb-2">资深设计师</p>
                <p className="text-xs text-foreground-500 leading-relaxed">UI/UX设计师，擅长女性品牌视觉体系建设</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100" id="contact">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-2">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                如果你有任何关于女性网站色彩设计的问题，或希望与我们合作，欢迎留言
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-1 flex flex-col gap-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-xs text-foreground-500">hello@color-aesthetics.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                    <p className="text-xs text-foreground-500">色彩美学 ColorAesthetics</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">办公地址</h4>
                    <p className="text-xs text-foreground-500">上海市静安区南京西路1788号</p>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                {formStatus === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-heading font-semibold text-foreground-900 mb-2">留言已发送</h4>
                    <p className="text-sm text-foreground-600">感谢你的留言，我们会在48小时内回复你</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    action={CONTACT_FORM_URL}
                    method="POST"
                    className="flex flex-col gap-4"
                    data-readdy-form="contact-about"
                    id="contact-form-about"
                  >
                    <div className="honeypot-field" aria-hidden="true">
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                        aria-hidden="true"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        placeholder="请输入你的姓名"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        placeholder="请输入你的邮箱地址"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="请输入你的留言内容（不超过500字）"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中
                        </>
                      ) : (
                        '发送留言'
                      )}
                    </button>

                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{errorMsg}</p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于色彩美学 - 美女网站色彩设计专业平台',
            description: '色彩美学是国内首家专注于女性网站色彩设计的专业知识平台，致力于为设计师提供配色方案、设计技巧和行业洞察。',
            url: 'https://color-aesthetics.com/about',
            about: {
              '@type': 'Organization',
              name: '色彩美学 ColorAesthetics',
              url: 'https://color-aesthetics.com',
              description: '专注于美女网站色彩设计的专业知识平台',
              foundingDate: '2024',
              contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'customer service',
                email: 'hello@color-aesthetics.com',
                availableLanguage: ['Chinese'],
              },
              address: {
                '@type': 'PostalAddress',
                addressLocality: '上海',
                addressRegion: '静安区',
                streetAddress: '南京西路1788号',
                addressCountry: 'CN',
              },
            },
          }),
        }}
      />
    </>
  );
}